import { type ReactNode } from "react";
import { type CSSObject } from "@emotion/react";
import { Colors, fontMedium, fontXSmall, TextAreaInput, type TextAreaInputProps } from "main";

/********* Types ********/
export type RadioInputProps = {
  /**
   * Label of the radio input.
   */
  label: string;
  /**
   * Value of this radio input.
   */
  value: string | number;
  /**
   * Optional description displayed below the label.
   * Can include links or other markup, so accepts a ReactNode.
   * @default undefined
   */
  description?: ReactNode;
  /**
   * Callback when the selected option changes.
   */
  onChange: (value: string | number) => void;
  /**
   * Optional text field to display when the radio input is selected.
   * @default undefined
   */
  hasTextField?: TextAreaInputProps;
  isSelected?: boolean;
  /**
   * Visual and interactive state of the input.
   * @default "enabled"
   */
  status?: "enabled" | "readOnly" | "disabled";
  /**
   * Used to group radio inputs.
   * @default undefined
   */
  groupName?: string;
  "data-testid"?: string;
};

/**
 * Component | RadioInput
 *
 * A radio button component with optional description and conditional text field.
 *
 * @internal Intended for internal use within the RadioList component only.
 * Not exported outside the project as a standalone component.
 *
 * @Figma https://www.figma.com/design/uJsnuoosxqn26Rot52zCXZ/%F0%9F%A7%B9-Swing-Design-System-v2?node-id=11456-3037&t=wsDIuxOlPRdHgHUZ-4
 */
export function RadioInput(props: RadioInputProps) {
  const {
    label,
    description,
    value,
    isSelected,
    status = "enabled",
    hasTextField,
    onChange,
    groupName,
    "data-testid": dataTestId,
  } = props;

  const isDisabled = status === "disabled";
  const isReadOnly = status === "readOnly";

  function handleChange() {
    // Onchange will not be called on readOnly or disabled inputs
    if (status === "enabled") {
      onChange(value);
    }
  }

  return (
    <label
      data-testid={
        dataTestId ? `${dataTestId}-${value}` : groupName ? `${groupName}-${value}` : undefined
      }
      css={styles.container({ isDisabled, isReadOnly })}
    >
      <input
        name={groupName}
        type="radio"
        value={value}
        checked={isSelected}
        onChange={handleChange}
        disabled={isDisabled}
        readOnly={isReadOnly}
        aria-label={label}
        css={styles.hiddenInput}
      />

      {/* Radio Input */}
      <div css={styles.radioWrapper}>
        <div css={styles.radioOuter({ isDisabled, isReadOnly, isSelected })}>
          <div css={styles.radioInner({ isSelected, isDisabled, isReadOnly })} />
        </div>
      </div>

      {/* Content */}
      <div css={{ color: isDisabled ? Colors.slate300 : Colors.black, width: "100%" }}>
        <p css={{ ...fontMedium(), margin: 0 }}>{label}</p>
        {description && <p css={{ margin: 0, ...fontXSmall() }}>{description}</p>}
        {hasTextField && isSelected && (
          <div css={{ paddingTop: 16 }}>
            <TextAreaInput {...hasTextField} />
          </div>
        )}
      </div>
    </label>
  );
}

/********* Styles ********/
const RADIO_SIZE = 18; // Size of the actual radio input
const RADIO_PADDING = 3; // Padding to make total clickable area 24px (mimic design)
const RADIO_INNER_SIZE = 9; // Half of the radio size for the inner circle

const styles = {
  container: ({
    isDisabled,
    isReadOnly,
  }: {
    isDisabled?: boolean;
    isReadOnly?: boolean;
  }): CSSObject => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    padding: "16px 24px",
    background: Colors.white200,
    cursor: isDisabled || isReadOnly ? "not-allowed" : "pointer",
    transition: "background-color 0.2s ease",
    ":hover": !isDisabled && !isReadOnly ? { background: Colors.white300 } : {},
  }),

  // Use the "visually hidden" pattern to hide the native radio input
  // while preserving keyboard accessibility and screen reader support.
  hiddenInput: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
  } as CSSObject,

  radioWrapper: {
    width: RADIO_SIZE + RADIO_PADDING * 2, // Total width 24px
    height: RADIO_SIZE + RADIO_PADDING * 2, // Total height 24px
    padding: RADIO_PADDING,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as CSSObject,

  radioOuter: ({
    isDisabled,
    isReadOnly,
    isSelected,
  }: {
    isDisabled?: boolean;
    isReadOnly?: boolean;
    isSelected?: boolean;
  }): CSSObject => ({
    width: RADIO_SIZE,
    height: RADIO_SIZE,
    borderRadius: "50%",
    boxSizing: "border-box",
    border: `2px solid ${isDisabled ? Colors.slate300 : isReadOnly ? Colors.black : Colors.blue500}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    transition: "border-color 0.2s ease",
    position: "relative",
    ...(isSelected && !isDisabled ? { ":hover": { background: Colors.white300 } } : {}),
  }),

  radioInner: ({
    isSelected,
    isDisabled,
    isReadOnly,
  }: {
    isSelected?: boolean;
    isDisabled?: boolean;
    isReadOnly?: boolean;
  }): CSSObject => ({
    width: RADIO_INNER_SIZE,
    height: RADIO_INNER_SIZE,
    borderRadius: "50%",
    backgroundColor: isSelected
      ? isDisabled
        ? Colors.slate300
        : isReadOnly
          ? Colors.black
          : Colors.blue500
      : Colors.white200,
    position: "absolute",
    top: "50%",
    left: "50%",
    transition: "transform 280ms cubic-bezier(0.4, 0, 0.2, 1) 0s",
    transform: isSelected
      ? "translate(-50%, -50%) scale3d(1, 1, 1)"
      : "translate(-50%, -50%) scale3d(0, 0, 0)",
  }),
};
